<script setup>
import { onKeyStroke } from '@vueuse/core';
import { debounce, filter, pick, pickBy, startsWith } from 'lodash-es';

import { ref } from 'vue';
import { DynamicScroller } from 'vue-virtual-scroller';
import { useTerraStore } from '~/terra/store/terra.store';
import { useThermStore } from '~/therm/store/therm.store';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  module: {
    type: String,
    default: 'terra',
  },
});

const emit = defineEmits(['close']);

const terra_store = useTerraStore();
const therm_store = useThermStore();

const form = ref({ search_input: '' });
const features_list = ref([]);
const selected_index = ref(0);
const selected_feature = ref({});
const scroller$ = ref({});
const is_focused = ref(true);
const is_loading = ref(false);

const store = computed(() => {
  return props.module === 'terra' ? terra_store : therm_store;
});

async function flyToFeature() {
  if (Object.keys(selected_feature.value).length) {
    const turf = (await import('@turf/turf'));
    store.value.map.flyTo(
      {
        center: turf.centroid(selected_feature.value).geometry.coordinates,
        essential: true,
        zoom: props.module === 'terra' ? 18 : 20,
      },
    );
    store.value.set_selected_features([selected_feature.value]);
  }
  emit('close');
}

function getMatchedSerialNumber(feature, term) {
  if (!term)
    return '';
  const serial_numbers = Object.values(feature.properties?.extraProperties?._serial_numbers || {});
  const lowered = term.toLowerCase();
  return serial_numbers.find(sn => sn && String(sn).toLowerCase().includes(lowered)) || '';
}

async function setFeature(feature) {
  const search_term = form.value.search_input;
  form.value.search_input = feature.properties.name;
  selected_feature.value = feature;
  if (props.module === 'terra')
    terra_store.searched_serial_number = getMatchedSerialNumber(feature, search_term);
  await flyToFeature();
}

onKeyStroke('ArrowDown', () => {
  selected_index.value = selected_index.value < features_list.value.length - 1 ? selected_index.value + 1 : selected_index.value;
  scroller$.value.scrollToItem(selected_index.value);
});

onKeyStroke('ArrowUp', () => {
  selected_index.value = selected_index.value !== 0 ? selected_index.value - 1 : 0;
  scroller$.value.scrollToItem(selected_index.value);
});

onKeyStroke('Enter', async () => {
  await setFeature(features_list.value[selected_index.value]);
});

function check(str) {
  return str && String(str).toLowerCase().includes(form.value.search_input.toLowerCase());
}

function searchTerraFeatures() {
  features_list.value = filter(terra_store.features_on_map, (f) => {
    const match_found = (check(f.properties.name) || check(f.properties.description));
    if (!match_found) {
      const extraProperties = pickBy((f.properties.extraProperties || {}), (value, key) => !startsWith(key, '_'));
      const serial_numbers = Object.values(f.properties.extraProperties?._serial_numbers || {});
      return Object.entries(extraProperties).some(([key, value]) => check(key) || check(value)) || serial_numbers.some(sn => check(sn));
    }
    return true;
  });
}

function searchThermFeatures() {
  features_list.value = filter(therm_store.features, (f) => {
    const match_found = (check(f.properties.name) || check(getDisplayName(f)) || check(f.properties.description));
    if (!match_found) {
      const properties = pick(f.properties, ['serial_number']);
      return Object.values(properties).some(value => check(value));
    }
    return true;
  });
}

function getDisplayName(feature) {
  if (props.module === 'terra') {
    return feature?.properties?.name || 'Untitled';
  }
  else if (props.module === 'therm') {
    const data = feature.properties;
    return `${data.modified_string_number || data.string_number} ${
      therm_store.feature_types[data.featureTypeId]
        ? ` : ${therm_store.feature_types[data.featureTypeId].name}`
        : ''
    }`;
  }
}

const updateFilterList = debounce(() => {
  is_loading.value = true;
  features_list.value = [];
  selected_index.value = 0;
  if (form.value.search_input) {
    if (props.module === 'terra') {
      searchTerraFeatures();
    }
    else if (props.module === 'therm') {
      searchThermFeatures();
    }
  }
  is_loading.value = false;
  terra_store.terra_track_events('Features searched');
}, Math.ceil(terra_store.features_on_map.length / 10));
</script>

<template>
  <hawk-modal-container :width="400" content_class="rounded-xl min-w-[600px]" @close="$emit('close')">
    <Vueform v-model="form" sync>
      <TextElement
        :key="is_focused ? 1 : 2"
        name="search_input"
        autocomplete="off"
        class="border-b"
        :placeholder="$t('Search')"
        :add-classes="{
          TextElement: {
            inputContainer: '!h-[56px] !border-0 py-3 focus:!ring-0',
          },
        }"
        :attrs="{ autofocus: true }"
        :focused="is_focused"
        :override-class="{
          inputContainer_focused: 'ring-0',
        }"
        @change="is_loading = true; updateFilterList()"
      >
        <template #addon-before>
          <IconHawkSearchLg />
        </template>
        <template #addon-after>
          <HawkButton icon size="xs" type="outlined">
            <IconHawkKeyMac class="w-5 h-5" />
          </HawkButton>
        </template>
      </TextElement>
    </Vueform>
    <div class="h-[125px] overflow-y-auto scrollbar my-3 ml-5">
      <HawkLoader v-if="is_loading" class="!m-0" />
      <div v-else-if="!features_list.length && form.search_input" class="flex items-center justify-center text-sm h-24 text-gray-500 -ml-5">
        No results found for '{{ form.search_input }}'
      </div>
      <template v-else>
        <DynamicScroller
          ref="scroller$"
          v-slot="{ item, index }"
          class="h-full scrollbar"
          :items="features_list.map((f) => ({ uid: f.properties.uid, ...f }))"
          :min-item-size="42"
          key-field="uid"
          :prerender="10"
        >
          <div
            class="flex text-sm font-medium items-center justify-between px-2 rounded-lg py-2.5 mr-2 cursor-pointer hover:bg-gray-50 text-gray-700"
            :class="{ 'bg-gray-100': selected_index === index }"
            @click="$emit('close'); setFeature(item);"
          >
            <div class="text-[14px]">
              {{ getDisplayName(item) }}
            </div>
            <div class="text-gray-600 invisible group-hover: mr-4" :class="{ '!visible': selected_index === index }">
              <IconHawkArrowRight class="w-5 h-5" />
            </div>
          </div>
        </DynamicScroller>
      </template>
    </div>
    <div class="flex items-center m-3">
      <HawkButton icon size="xs" type="outlined" class="p-[6px] mr-1.5">
        <IconHawkArrowUp class="w-[16px] h-[16px]" />
      </HawkButton>

      <HawkButton icon size="xs" class="p-[6px]" type="outlined">
        <IconHawkArrowDown class="w-[16px] h-[16px]" />
      </HawkButton>

      <span class="text-sm text-gray-500 font-semibold mx-2">{{ $t('to navigate') }}</span>

      <HawkButton icon size="xs" class="p-[6px]" type="outlined">
        <IconHawkCornerDownLeft class="w-[16px] h-[16px]" />
      </HawkButton>

      <span class="text-sm text-gray-500 font-semibold mx-2">{{ $t('to select') }}</span>

      <HawkButton class="h-[28px] p-[6px]" icon type="outlined">
        <span class="text-sm text-gray-500 font-semibold"> {{ $t('esc') }}</span>
      </HawkButton>

      <span class="text-sm text-gray-500 font-semibold mx-2">{{ $t('to close') }}</span>
    </div>
  </hawk-modal-container>
</template>
